import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { errText } from "@/lib/api";
import { Panel, Btn, fmt, useOps } from "./ui";
import BulkUpload from "./BulkUpload";
import StationListen from "./StationListen";
import { ACCEPT } from "./bulkMatch";

function SlotPreview({ s }) {
  const { api } = useOps();
  const [open, setOpen] = useState(false);
  const src = `${api.defaults.baseURL}/content/${s.slot}/file?v=${encodeURIComponent(s.updated_at || "")}`;
  if (s.kind === "audio") return <audio data-testid={`content-audio-${s.slot}`} controls preload="none" src={src} className="h-8 w-60 shrink-0" />;
  if (s.kind === "image") return <a href={src} target="_blank" rel="noreferrer"><img data-testid={`content-image-${s.slot}`} src={src} alt={s.slot} className="h-10 w-10 shrink-0 rounded object-cover" /></a>;
  return open ? <video data-testid={`content-video-${s.slot}`} controls autoPlay src={src} className="h-32 shrink-0 rounded bg-black" />
    : <Btn variant="ghost" onClick={() => setOpen(true)} data-testid={`content-video-open-${s.slot}`}>Play</Btn>;
}

function StationBoard({ slots }) {
  const stations = slots.filter((s) => s.slot.startsWith("station_"));
  const missing = stations.filter((s) => !s.filled);
  const has = (k) => (slots.find((s) => s.slot === k)?.filled ? "uploaded" : "missing");
  return (
    <Panel title={`Radio readiness — ${stations.length - missing.length}/${stations.length} stations`} testid="radio-station-board">
      <div className="grid grid-cols-7 gap-1 sm:grid-cols-10 lg:grid-cols-[repeat(25,minmax(0,1fr))]">
        {stations.map((s) => {
          const f = s.slot.slice(8);
          return <span key={s.slot} title={s.label} data-testid={`station-chip-${f}`} className={`mono rounded px-1 py-0.5 text-center text-[10px] ${s.filled ? "bg-green-500/20 text-green-300" : "bg-[#1a2332] text-slate-300"} ${f === "333" ? "ring-1 ring-sky-400" : ""}`}>{f}</span>;
        })}
      </div>
      <p className="mt-2 text-[11px] text-slate-500">
        Static {has("radio_static")} · Tuning {has("radio_tuning")}. Missing stations play a placeholder tone on radio333 (333 uses a computer voice). Use the players below to check each file before going live.
      </p>
    </Panel>
  );
}

// 333 Helpers images: click the picture to choose which point stays in frame when the site crops it.
function FocusPicker({ s, onDone }) {
  const { api } = useOps();
  const [open, setOpen] = useState(false);
  const [focus, setFocus] = useState(s.focus);
  const src = `${api.defaults.baseURL}/content/${s.slot}/file?v=${encodeURIComponent(s.updated_at || "")}`;
  const [x, y] = focus.split(" ").map((v) => parseFloat(v));
  const pick = async (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    const nx = Math.round(((e.clientX - r.left) / r.width) * 100), ny = Math.round(((e.clientY - r.top) / r.height) * 100);
    try { const res = await api.put(`/helpers-focus/${s.slot}`, { x: nx, y: ny }); setFocus(res.data.focus); toast.success(`Focal point ${res.data.focus}`); onDone(); }
    catch (err) { toast.error(errText(err)); }
  };
  return (
    <>
      <Btn variant="ghost" onClick={() => setOpen((o) => !o)} data-testid={`content-focus-toggle-${s.slot}`}>Focus {focus}</Btn>
      {open && (
        <div className="flex w-full flex-wrap items-start gap-4 pb-3 pl-5" data-testid={`content-focus-panel-${s.slot}`}>
          <div className="relative cursor-crosshair" onClick={pick} data-testid={`content-focus-image-${s.slot}`}>
            <img src={src} alt={s.slot} className="block max-h-64 max-w-[420px] rounded" draggable={false} />
            <span className="pointer-events-none absolute h-5 w-5 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white shadow-[0_0_0_2px_#000]" style={{ left: `${x}%`, top: `${y}%` }} />
          </div>
          <div className="space-y-2 text-[11px] text-slate-400">
            <p>Click the picture on the key subject. Crops on the site keep that point in frame.</p>
            <div className="flex gap-2">
              {(s.slot.match(/_(16_founder|1[7-9]_board|2[01]_board)/) ? [["circle", "aspect-square w-20 rounded-full"]] : [["4:3", "aspect-[4/3] w-28"], ["16:9", "aspect-video w-32"], ["4:5", "aspect-[4/5] w-20"]]).map(([label, cls]) => (
                <div key={label}><img src={src} alt="" className={`${cls} rounded object-cover`} style={{ objectPosition: focus }} /><p className="mt-0.5 text-center">{label}</p></div>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function Slot({ s, onDone }) {
  const { api } = useOps();
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    const fd = new FormData(); fd.append("file", file);
    try { await api.post(`/content/${s.slot}`, fd); toast.success(`${s.slot} uploaded`); onDone(); }
    catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };
  const clear = async () => { if (!window.confirm(`Clear ${s.slot}?`)) return; await api.delete(`/content/${s.slot}`); onDone(); };
  return (
    <div data-testid={`admin-content-upload-slot-${s.slot}`} className="flex flex-wrap items-center gap-3 border-b border-[#243144] py-1.5 text-xs last:border-0">
      <span className={`h-2 w-2 shrink-0 rounded-full ${s.filled ? "bg-green-400" : "bg-slate-600"}`} />
      <span className="w-72 shrink-0 truncate text-slate-200" title={s.label}>{s.label}</span>
      <span className="min-w-0 flex-1 truncate text-slate-500">{s.filled ? `${s.filename} · ${fmt(s.updated_at)}` : "placeholder in use"}</span>
      <label className={`cursor-pointer rounded border border-[#243144] bg-[#1a2332] px-2 py-1 hover:bg-[#223047] ${busy ? "opacity-40" : ""}`}>
        {busy ? "Uploading…" : s.filled ? "Replace" : "Upload"}
        <input type="file" accept={ACCEPT[s.kind]} className="hidden" disabled={busy} data-testid={`content-file-${s.slot}`} onChange={(e) => upload(e.target.files?.[0])} />
      </label>
      {s.filled && <Btn variant="ghost" onClick={clear} data-testid={`content-clear-${s.slot}`}>Clear</Btn>}
      {s.filled && <SlotPreview s={s} />}
      {s.filled && s.focus && <FocusPicker s={s} onDone={onDone} />}
    </div>
  );
}

export default function Content() {
  const { api } = useOps();
  const [slots, setSlots] = useState([]);
  const load = useCallback(() => api.get("/content").then((r) => setSlots(r.data)), [api]);
  useEffect(() => { load(); }, [load]);
  const groups = [["Teaser & test face", (s) => s.slot.startsWith("teaser") || ["artifact_test_face", "log_reward", "eve_intro"].includes(s.slot)],
    ["Artifact artwork (edit layouts in the Artifacts tab)", (s) => s.slot.startsWith("template_")],
    ["Hint videos", (s) => s.slot.startsWith("hint_")], ["Radio audio", (s) => s.slot.startsWith("radio_") || s.slot.startsWith("station_")],
    ["333 Helpers site", (s) => s.slot.startsWith("helpers_")]];
  return (
    <div className="space-y-3" data-testid="admin-content-slots">
      <StationBoard slots={slots} />
      <StationListen slots={slots} />
      {groups.map(([title, f]) => (
        <Panel key={title} title={`${title} (${slots.filter(f).filter((s) => s.filled).length}/${slots.filter(f).length} uploaded)`}
          actions={<BulkUpload title={title} slots={slots.filter(f)} onDone={load} />}>
          {slots.filter(f).map((s) => <Slot key={s.slot} s={s} onDone={load} />)}
        </Panel>
      ))}
    </div>
  );
}
